/**
 * The module catalogue — 12 families across the 5 slot kinds.
 *
 * The load-bearing constraint from docs/04 §5.2 is that a family is
 * *thematically* parallel across melee and projectile but *mechanically*
 * different. So every module carries two independent effect descriptors and
 * they are authored separately, never derived from one another. A shared blob
 * with a couple of archetype conditionals would read the same in the UI and
 * play the same in both hands, which is exactly the failure the spec calls out.
 *
 * Self-risk (§5.3) is not a downside tacked onto weak modules — the highest
 * rarities here are the ones that can kill you.
 */

import { DamageType } from '../entity/ecs';
import { MAT } from '../sim/materialData';
import { PrimerKind, StatusKind, weaponTags } from './types';
import type {
  Archetype, ModuleDef, ModuleEffect, ModuleFamily, Rarity, SelfRisk, SlotKind,
} from './types';

interface ModSpec {
  id: string;
  family: ModuleFamily;
  slot: SlotKind;
  name: string;
  rarity?: Rarity;
  fits?: Archetype | 'both';
  melee: ModuleEffect;
  proj: ModuleEffect;
  risk?: SelfRisk;
  tags?: number;
  desc: string;
}

function mod(s: ModSpec): ModuleDef {
  return {
    id: s.id,
    family: s.family,
    slot: s.slot,
    name: s.name,
    rarity: s.rarity ?? 'common',
    fits: s.fits ?? 'both',
    onMelee: s.melee,
    onProjectile: s.proj,
    selfRisk: s.risk,
    tags: s.tags ?? 0,
    desc: s.desc,
  };
}

export function buildModules(): ModuleDef[] {
  const P = PrimerKind;
  const D = DamageType;
  const out: ModuleDef[] = [];

  // ------------------------------------------------------------- frost

  out.push(mod({
    id: 'frost_core_rime', family: 'frost', slot: 'core', name: 'Rime Core',
    rarity: 'uncommon',
    melee: {
      summary: 'Swings leave an ice trail and open a brittle window on the target.',
      damageTypeOverride: D.FROST, damageMul: 0.92,
      primes: [P.FROZEN],
      statusOnHit: { kind: StatusKind.FROZEN, duration: 2.2, stacks: 1, chance: 0.5 },
      melee: { trailMat: MAT.ICE, arcMul: 1.05 },
      terrain: { heat: -90 },
    },
    proj: {
      summary: 'Cryo rounds: a chill burst on impact, and shards skim off ICE instead of stopping.',
      damageTypeOverride: D.FROST, damageMul: 0.95,
      primes: [P.FROZEN],
      statusOnHit: { kind: StatusKind.FROZEN, duration: 1.6, stacks: 1, chance: 0.65 },
      projectile: { ricochet: 2, speedMul: 1.05 },
      terrain: { heat: -140 },
    },
    desc: 'Converts damage to frost. The brittle window is the whole reason to run it.',
  }));

  out.push(mod({
    id: 'frost_payload_shatter', family: 'frost', slot: 'payload', name: 'Shatterpoint Payload',
    rarity: 'rare',
    melee: {
      summary: 'Hitting a FROZEN target shatters it for a cone of splinters.',
      detonates: [P.FROZEN], detonationMul: 1.5, primedDamageMul: 1.35,
      melee: { arcMul: 1.15 },
    },
    proj: {
      summary: 'Rounds that strike a FROZEN target split into three shards.',
      detonates: [P.FROZEN], detonationMul: 1.3,
      projectile: { splitOnImpact: 3, countAdd: 0 },
    },
    desc: 'A detonator. Useless without something in the other hand that freezes.',
  }));

  out.push(mod({
    id: 'frost_kinetic_glacier', family: 'frost', slot: 'kinetic', name: 'Glacier Bearing',
    melee: {
      summary: 'Heavier, slower swings that plant you — and freeze the ground you stand on.',
      cadenceMul: 0.82, damageMul: 1.22, knockbackMul: 1.3, weightAdd: 0.12,
      terrain: { heat: -60, displaces: false },
    },
    proj: {
      summary: 'Slower, heavier rounds that punch through and chill everything they pass.',
      cadenceMul: 0.78, damageMul: 1.3, projectile: { pierceAdd: 1, speedMul: 0.8, gravityScaleMul: 1.4 },
      terrain: { heat: -60 },
    },
    desc: 'Trades rate of fire for mass. On a light weapon it changes the class entirely.',
  }));

  out.push(mod({
    id: 'frost_trigger_parryfreeze', family: 'frost', slot: 'trigger', name: 'Coldsnap Trigger',
    rarity: 'rare',
    melee: {
      summary: 'On parry: freeze whatever you parried solid for two seconds.',
      trigger: 'onParry',
      statusOnHit: { kind: StatusKind.FROZEN, duration: 2.0, stacks: 2, chance: 1 },
      melee: { parryWindowAdd: 40 },
    },
    proj: {
      summary: 'On crit: a chill burst freezes everything within a short radius.',
      trigger: 'onCrit',
      statusOnHit: { kind: StatusKind.FROZEN, duration: 1.4, stacks: 1, chance: 1 },
      terrain: { heat: -180 },
    },
    desc: 'Parrying is already good. This makes the follow-up free.',
  }));

  // -------------------------------------------------------------- pyre

  out.push(mod({
    id: 'pyre_core_ember', family: 'pyre', slot: 'core', name: 'Ember Core',
    rarity: 'uncommon',
    melee: {
      summary: 'The swing path ignites, and heat accumulates on the weapon with every hit.',
      damageTypeOverride: D.FIRE, damageMul: 1.0,
      primes: [P.BURNING],
      statusOnHit: { kind: StatusKind.BURNING, duration: 3.0, stacks: 1, chance: 0.6 },
      melee: { trailMat: MAT.FIRE, chargePerHit: 0.12 },
      terrain: { heat: 260 },
    },
    proj: {
      summary: 'Incendiary rounds that leave FIRE cells and set off any accelerant they touch.',
      damageTypeOverride: D.FIRE, damageMul: 1.05,
      primes: [P.BURNING],
      statusOnHit: { kind: StatusKind.BURNING, duration: 2.4, stacks: 1, chance: 0.75 },
      emit: { mat: MAT.FIRE, count: 3, velocity: 40 },
      terrain: { heat: 340 },
    },
    desc: 'The most straightforward core in the game and still one of the best.',
  }));

  out.push(mod({
    id: 'pyre_payload_backdraft', family: 'pyre', slot: 'payload', name: 'Backdraft Payload',
    rarity: 'rare',
    melee: {
      summary: 'Striking an OILED or BURNING target sets off a fireball centred on it.',
      detonates: [P.OILED, P.BURNING], detonationMul: 1.7,
      emit: { mat: MAT.FIRE, count: 22, velocity: 90 },
      terrain: { heat: 520, destroy: { maxHardness: 3, radius: 9, chance: 0.6 } },
    },
    proj: {
      summary: 'Impacts on OILED ground bloom into a spreading fire pool.',
      detonates: [P.OILED, P.BURNING], detonationMul: 1.45,
      emit: { mat: MAT.FIRE, count: 30, velocity: 60 },
      terrain: { heat: 460, destroy: { maxHardness: 2, radius: 12, chance: 0.4 } },
    },
    risk: {
      kind: 'friendlyFire', magnitude: 0.45,
      warning: 'The fireball does not check your team. Standing in your own oil is fatal.',
    },
    tags: weaponTags('SELF_RISK'),
    desc: 'Pair with a sprayer. Then remember where you sprayed.',
  }));

  out.push(mod({
    id: 'pyre_frame_forge', family: 'pyre', slot: 'frame', name: 'Forgeheart Frame',
    rarity: 'exotic',
    melee: {
      summary: 'Damage scales with accumulated heat, up to +50%. The weapon never cools on its own.',
      damageMul: 1.15, cadenceMul: 1.1,
      melee: { chargePerHit: 0.2 },
      statusOnHit: { kind: StatusKind.BURNING, duration: 2.0, stacks: 1, chance: 0.35 },
    },
    proj: {
      summary: 'Sustained fire raises the barrel temperature; damage climbs, accuracy decays.',
      damageMul: 1.2, cadenceMul: 1.15,
      projectile: { spreadMul: 1.35 },
    },
    risk: {
      kind: 'overheat', magnitude: 0.09, status: StatusKind.OVERHEATED,
      warning: 'At full heat the weapon burns its wielder until it is holstered.',
    },
    tags: weaponTags('SELF_RISK'),
    desc: 'A build that wants you to never stop attacking, and punishes you for obeying.',
  }));

  out.push(mod({
    id: 'pyre_kinetic_flare', family: 'pyre', slot: 'kinetic', name: 'Flare Vent',
    melee: {
      summary: 'Swings vent flame backwards, shoving you forward through the arc.',
      cadenceMul: 1.12, knockbackMul: 0.8,
      emit: { mat: MAT.FIRE, count: 4, velocity: 120 },
    },
    proj: {
      summary: 'Muzzle flare doubles recoil — aim down and it is a jump.',
      projectile: { recoilMul: 2.0, spreadMul: 1.1 },
      emit: { mat: MAT.FIRE, count: 6, velocity: 150 },
    },
    tags: weaponTags('MOBILITY'),
    desc: 'Movement module first, damage module never.',
  }));

  // -------------------------------------------------------------- void

  out.push(mod({
    id: 'void_core_phase', family: 'void', slot: 'core', name: 'Phasecut Core',
    rarity: 'rare',
    melee: {
      summary: 'Swings pass through terrain and land a delayed second cut a moment later.',
      damageTypeOverride: D.VOID, damageMul: 0.88,
      primes: [P.PHASED],
      melee: { echoDelayMs: 320, arcMul: 1.1 },
      statusOnHit: { kind: StatusKind.PHASED, duration: 2.0, stacks: 1, chance: 0.5 },
    },
    proj: {
      summary: 'Distortion bolts that bend toward mass and warp their own trajectory.',
      damageTypeOverride: D.VOID, damageMul: 0.9,
      primes: [P.PHASED],
      projectile: { homing: 0.35, gravityScaleMul: 0, lifetimeMul: 1.3 },
      statusOnHit: { kind: StatusKind.PHASED, duration: 1.6, stacks: 1, chance: 0.5 },
    },
    desc: 'Hitting through walls sounds broken until you realise you cannot see through them.',
  }));

  out.push(mod({
    id: 'void_payload_collapse', family: 'void', slot: 'payload', name: 'Collapse Payload',
    rarity: 'exotic',
    melee: {
      summary: 'The finisher slams a gravity well down, dragging loose cells and bodies inward.',
      detonates: [P.PHASED], detonationMul: 1.6,
      knockbackMul: -1.4, poiseMul: 1.5,
      terrain: { displaces: true, destroy: { maxHardness: 4, radius: 14, chance: 0.5 } },
    },
    proj: {
      summary: 'Impacts leave a gravity pocket that pulls later shots into it.',
      detonates: [P.PHASED], detonationMul: 1.4,
      projectile: { homing: 0.6, lifetimeMul: 1.5 },
      terrain: { displaces: true, destroy: { maxHardness: 3, radius: 10, chance: 0.4 } },
    },
    desc: 'Negative knockback. Everything comes to you, including the things you were avoiding.',
  }));

  out.push(mod({
    id: 'void_frame_untether', family: 'void', slot: 'frame', name: 'Untethered Frame',
    rarity: 'exotic',
    melee: {
      summary: 'You phase through enemies during every swing. You also phase out of your own iframes.',
      damageMul: 1.28, weightAdd: -0.15, recoveryMul: 0.85,
    },
    proj: {
      summary: 'Rounds ignore the first surface they meet. So does incoming fire, on the way back.',
      damageMul: 1.25, projectile: { pierceAdd: 2, speedMul: 1.1 },
    },
    risk: {
      kind: 'selfStatus', magnitude: 1, status: StatusKind.PHASED,
      warning: 'Permanently PHASED: your invulnerability frames no longer apply.',
    },
    tags: weaponTags('SELF_RISK'),
    desc: 'A straight trade of survivability for output. The trade is not subtle.',
  }));

  out.push(mod({
    id: 'void_trigger_rewind', family: 'void', slot: 'trigger', name: 'Rewind Trigger',
    rarity: 'rare',
    melee: {
      summary: 'On kill: snap back to where you started the swing, at full momentum.',
      trigger: 'onKill', recoveryMul: 0.7,
    },
    proj: {
      summary: 'On kill: the shot that killed reappears at the muzzle and fires again.',
      trigger: 'onKill', projectile: { countAdd: 1 },
    },
    desc: 'Rewards commitment by undoing it, which is the only honest way to sell commitment.',
  }));

  // ------------------------------------------------------------- blood

  out.push(mod({
    id: 'blood_core_hema', family: 'blood', slot: 'core', name: 'Hematic Core',
    rarity: 'uncommon',
    melee: {
      summary: 'Bleeds everything you hit and returns a slice of the damage as health.',
      damageTypeOverride: D.BLOOD, damageMul: 1.0, lifestealFrac: 0.06,
      primes: [P.BLEEDING],
      statusOnHit: { kind: StatusKind.BLEEDING, duration: 4.0, stacks: 1, chance: 0.7 },
      emit: { mat: MAT.BLOOD, count: 3, velocity: 50 },
    },
    proj: {
      summary: 'Hematic rounds cost health to chamber and hit far harder for it.',
      damageTypeOverride: D.BLOOD, damageMul: 1.35, lifestealFrac: 0.03,
      primes: [P.BLEEDING],
      statusOnHit: { kind: StatusKind.BLEEDING, duration: 3.0, stacks: 1, chance: 0.5 },
    },
    risk: {
      kind: 'healthCost', magnitude: 0.012,
      warning: 'Each projectile costs 1.2% of your maximum health.',
    },
    tags: weaponTags('SELF_RISK'),
    desc: 'The lifesteal pays for the cost only if you are actually connecting.',
  }));

  out.push(mod({
    id: 'blood_payload_frenzy', family: 'blood', slot: 'payload', name: 'Frenzy Payload',
    rarity: 'rare',
    melee: {
      summary: 'While you are bleeding, damage is up 60% and lifesteal triples.',
      damageMul: 1.6, lifestealFrac: 0.18, cadenceMul: 1.2,
      detonates: [P.BLEEDING], detonationMul: 1.5,
    },
    proj: {
      summary: 'Spends health on fire: bursts scale with how much you have already lost.',
      damageMul: 1.45,
      detonates: [P.BLEEDING], detonationMul: 1.35,
      projectile: { countAdd: 1, spreadMul: 1.2 },
    },
    risk: {
      kind: 'selfStatus', magnitude: 1, status: StatusKind.BLEEDING,
      warning: 'Applies BLEEDING to you on every finisher. That is not a side effect, it is the cost.',
    },
    tags: weaponTags('SELF_RISK'),
    desc: 'The build the design doc names by name. It is a build, and it can kill you.',
  }));

  out.push(mod({
    id: 'blood_trigger_sacrifice', family: 'blood', slot: 'trigger', name: 'Sacrifice Trigger',
    rarity: 'exotic',
    melee: {
      summary: 'At low health every hit is a guaranteed critical.',
      trigger: 'onLowHealth', critChanceAdd: 1.0, critMulAdd: 0.3,
    },
    proj: {
      summary: 'At low health shots pierce everything and cost double health.',
      trigger: 'onLowHealth', projectile: { pierceAdd: 5 }, damageMul: 1.4,
    },
    risk: {
      kind: 'healthCost', magnitude: 0.03,
      warning: 'Only active below 30% health, and it keeps taking you lower.',
    },
    tags: weaponTags('SELF_RISK'),
    desc: 'Strongest module in the family, live-able for about four seconds at a time.',
  }));

  out.push(mod({
    id: 'blood_kinetic_curse', family: 'blood', slot: 'kinetic', name: 'Curseweight',
    melee: {
      summary: 'Heavier swings that curse on contact; cursed targets take amplified everything.',
      weightAdd: 0.1, damageMul: 1.1, primes: [P.CURSED],
      statusOnHit: { kind: StatusKind.CURSED, duration: 5.0, stacks: 1, chance: 0.4 },
    },
    proj: {
      summary: 'Rounds trail curse mist that lingers where they pass.',
      damageMul: 1.05, primes: [P.CURSED],
      projectile: { speedMul: 0.9 },
      emit: { mat: MAT.CURSED_RESIDUE, count: 2, velocity: 20 },
      statusOnHit: { kind: StatusKind.CURSED, duration: 4.0, stacks: 1, chance: 0.5 },
    },
    desc: 'Cursing is a setup action. The payoff is in whatever detonates it.',
  }));

  // ------------------------------------------------------------ fungal

  out.push(mod({
    id: 'fungal_core_bloom', family: 'fungal', slot: 'core', name: 'Bloom Core',
    rarity: 'uncommon',
    melee: {
      summary: 'Hits bloom fungal mat out of the wound and impale on the follow-up.',
      damageTypeOverride: D.FUNGAL, damageMul: 0.95,
      primes: [P.INFECTED],
      statusOnHit: { kind: StatusKind.INFECTED, duration: 5.0, stacks: 1, chance: 0.6 },
      emit: { mat: MAT.FUNGAL_MAT, count: 4, velocity: 20 },
      melee: { trailMat: MAT.SPORE_DUST },
    },
    proj: {
      summary: 'Rounds burst into spore clouds that keep spreading after impact.',
      damageTypeOverride: D.FUNGAL, damageMul: 0.9,
      primes: [P.INFECTED],
      statusOnHit: { kind: StatusKind.INFECTED, duration: 4.0, stacks: 1, chance: 0.7 },
      emit: { mat: MAT.SPORE_CLOUD, count: 10, velocity: 35 },
      projectile: { lifetimeMul: 1.2 },
    },
    desc: 'Slow damage that changes the room. Fungal builds win the second minute, not the first.',
  }));

  out.push(mod({
    id: 'fungal_payload_seeding', family: 'fungal', slot: 'payload', name: 'Corpse Seeding',
    rarity: 'rare',
    melee: {
      summary: 'Kills plant a bloom in the corpse; the bloom infects whatever walks past.',
      trigger: 'onKill', detonates: [P.INFECTED], detonationMul: 1.4,
      emit: { mat: MAT.FUNGAL_MAT, count: 14, velocity: 25 },
    },
    proj: {
      summary: 'Mutation payload: infected targets that die spread INFECTED to two neighbours.',
      trigger: 'onKill', detonates: [P.INFECTED], detonationMul: 1.25,
      emit: { mat: MAT.SPORE_CLOUD, count: 20, velocity: 45 },
      statusOnHit: { kind: StatusKind.INFECTED, duration: 6.0, stacks: 2, chance: 1 },
    },
    desc: 'Turns a crowd into a chain reaction, one corpse at a time.',
  }));

  out.push(mod({
    id: 'fungal_frame_myco', family: 'fungal', slot: 'frame', name: 'Mycelial Frame',
    melee: {
      summary: 'The weapon is alive: it repairs between fights and grows reach as it feeds.',
      reachMul: 1.12, damageMul: 1.08, weightAdd: -0.04,
    },
    proj: {
      summary: 'Living chamber regrows ammunition slowly and for free.',
      cadenceMul: 0.94, damageMul: 1.1, projectile: { lifetimeMul: 1.25 },
    },
    tags: weaponTags('ORGANIC'),
    desc: 'Small numbers, no downside, and it never asks you for resources.',
  }));

  out.push(mod({
    id: 'fungal_trigger_sporeburst', family: 'fungal', slot: 'trigger', name: 'Sporeburst Trigger',
    melee: {
      summary: 'On a primer detonation: a spore cloud erupts and infects everything nearby.',
      trigger: 'onDetonate',
      emit: { mat: MAT.SPORE_CLOUD, count: 26, velocity: 55 },
      statusOnHit: { kind: StatusKind.SPORED, duration: 4.0, stacks: 1, chance: 1 },
    },
    proj: {
      summary: 'On a primer detonation: the round splits into four spore seeds.',
      trigger: 'onDetonate',
      projectile: { splitOnImpact: 4 },
      emit: { mat: MAT.SPORE_DUST, count: 12, velocity: 40 },
    },
    desc: 'Rewards a build that already detonates. It does nothing on its own.',
  }));

  // ---------------------------------------------------------- galvanic

  out.push(mod({
    id: 'galv_core_arc', family: 'galvanic', slot: 'core', name: 'Arc Core',
    rarity: 'uncommon',
    melee: {
      summary: 'Charge builds on every hit and dumps into the heavy. Conducts through WET terrain.',
      damageTypeOverride: D.SHOCK, damageMul: 0.94,
      primes: [P.SHOCKED],
      melee: { chargePerHit: 0.18 },
      statusOnHit: { kind: StatusKind.SHOCKED, duration: 2.5, stacks: 1, chance: 0.55 },
      terrain: { charge: 0.8 },
    },
    proj: {
      summary: 'Arc rounds chain to two nearby targets and overload machines outright.',
      damageTypeOverride: D.SHOCK, damageMul: 0.9,
      primes: [P.SHOCKED],
      statusOnHit: { kind: StatusKind.SHOCKED, duration: 2.0, stacks: 1, chance: 0.7 },
      projectile: { ricochet: 2, homing: 0.2 },
      terrain: { charge: 1.0 },
    },
    desc: 'Wet floors are a damage multiplier. So is your own puddle.',
  }));

  out.push(mod({
    id: 'galv_payload_overload', family: 'galvanic', slot: 'payload', name: 'Overload Payload',
    rarity: 'rare',
    melee: {
      summary: 'Detonating SHOCKED chains lightning through every conductive body in reach.',
      detonates: [P.SHOCKED, P.OILED], detonationMul: 1.55,
      terrain: { charge: 1.0 },
      emit: { mat: MAT.LIGHTNING, count: 6, velocity: 200 },
    },
    proj: {
      summary: 'Detonating SHOCKED short-circuits machines and stuns them for a beat.',
      detonates: [P.SHOCKED], detonationMul: 1.4,
      statusOnHit: { kind: StatusKind.STAGGERED, duration: 1.2, stacks: 1, chance: 0.8 },
      terrain: { charge: 1.0 },
    },
    risk: {
      kind: 'selfDamage', magnitude: 6,
      warning: 'The chain conducts back through water you are standing in.',
    },
    tags: weaponTags('SELF_RISK'),
    desc: 'Do not run this with a water sprayer unless you like surprises.',
  }));

  out.push(mod({
    id: 'galv_kinetic_railcoil', family: 'galvanic', slot: 'kinetic', name: 'Railcoil',
    melee: {
      summary: 'Magnetically snaps the swing forward, closing distance on the active frame.',
      cadenceMul: 1.15, windupMul: 0.8, knockbackMul: 1.2,
    },
    proj: {
      summary: 'Coil acceleration: much faster rounds, much harder recoil.',
      projectile: { speedMul: 1.7, recoilMul: 1.6, pierceAdd: 1 },
    },
    tags: weaponTags('MOBILITY'),
    desc: 'Speed is accuracy on a projectile and reach on a melee.',
  }));

  out.push(mod({
    id: 'galv_trigger_static', family: 'galvanic', slot: 'trigger', name: 'Static Discharge',
    melee: {
      summary: 'On dash: discharge stored charge in a ring around you.',
      trigger: 'onDash',
      statusOnHit: { kind: StatusKind.SHOCKED, duration: 1.5, stacks: 1, chance: 1 },
      terrain: { charge: 0.7 },
    },
    proj: {
      summary: 'On dash: the next shot fires free and at double charge.',
      trigger: 'onDash', damageMul: 2.0, cadenceMul: 1.0,
    },
    desc: 'Makes dashing an offensive action, which is exactly what the movement wants.',
  }));

  // --------------------------------------------------------- corrosive

  out.push(mod({
    id: 'corr_core_etch', family: 'corrosive', slot: 'core', name: 'Etching Core',
    rarity: 'uncommon',
    melee: {
      summary: 'Etches armour permanently — every hit lowers the target\'s defence for good.',
      damageTypeOverride: D.ACID, damageMul: 0.9,
      primes: [P.CORRODED],
      statusOnHit: { kind: StatusKind.ARMOR_SHRED, duration: 999, stacks: 1, chance: 0.8 },
      terrain: { corrode: 0.6 },
    },
    proj: {
      summary: 'Acid globs that leave pools and strip armour on contact.',
      damageTypeOverride: D.ACID, damageMul: 0.95,
      primes: [P.CORRODED],
      statusOnHit: { kind: StatusKind.CORRODED, duration: 4.0, stacks: 1, chance: 0.75 },
      emit: { mat: MAT.ACID, count: 6, velocity: 60 },
      projectile: { gravityScaleMul: 1.3, speedMul: 0.85 },
      terrain: { corrode: 0.8 },
    },
    desc: 'The answer to anything that armours up. Permanent shred does not fall off.',
  }));

  out.push(mod({
    id: 'corr_payload_dissolve', family: 'corrosive', slot: 'payload', name: 'Dissolution Payload',
    rarity: 'rare',
    melee: {
      summary: 'Dissolves shields and blocks outright; detonating CORRODED melts a hole in cover.',
      detonates: [P.CORRODED], detonationMul: 1.45,
      terrain: { corrode: 1.0, destroy: { maxHardness: 5, radius: 8, chance: 0.7 } },
    },
    proj: {
      summary: 'Detonating CORRODED sprays a wider acid pool and eats the floor under it.',
      detonates: [P.CORRODED], detonationMul: 1.3,
      emit: { mat: MAT.ACID, count: 18, velocity: 70 },
      terrain: { corrode: 1.0, destroy: { maxHardness: 4, radius: 10, chance: 0.5 } },
    },
    desc: 'Also a digging module, which is not obvious until you try it on a wall.',
  }));

  out.push(mod({
    id: 'corr_frame_leaching', family: 'corrosive', slot: 'frame', name: 'Leaching Frame',
    melee: {
      summary: 'Acid-treated edge: more damage, and the weapon slowly corrodes itself.',
      damageMul: 1.24, reachMul: 0.96,
    },
    proj: {
      summary: 'Corroded barrel: more damage, degrading accuracy over a magazine.',
      damageMul: 1.2, projectile: { spreadMul: 1.25 },
    },
    risk: {
      kind: 'overheat', magnitude: 0.04,
      warning: 'The weapon loses 4% of its damage per fight until repaired at a rest node.',
    },
    tags: weaponTags('SELF_RISK'),
    desc: 'Front-loaded power that asks you to actually stop and maintain the thing.',
  }));

  // ------------------------------------------------------------ nanite

  out.push(mod({
    id: 'nano_core_swarm', family: 'nanite', slot: 'core', name: 'Swarm Core',
    rarity: 'rare',
    melee: {
      summary: 'Self-repairing edge. Corpses it touches get rebuilt as short-lived allies.',
      damageTypeOverride: D.NANITE, damageMul: 0.92,
      statusOnHit: { kind: StatusKind.NANITE_BOUND, duration: 4.0, stacks: 1, chance: 0.5 },
      emit: { mat: MAT.NANITE_DUST, count: 3, velocity: 25 },
    },
    proj: {
      summary: 'Nanite rounds infect machines and replicate off each kill.',
      damageTypeOverride: D.NANITE, damageMul: 0.95,
      statusOnHit: { kind: StatusKind.NANITE_BOUND, duration: 3.0, stacks: 1, chance: 0.6 },
      projectile: { splitOnImpact: 2, homing: 0.3 },
      emit: { mat: MAT.NANITE_SWARM, count: 4, velocity: 40 },
    },
    desc: 'Very strong against anything with MACHINE in its composition, mediocre otherwise.',
  }));

  out.push(mod({
    id: 'nano_payload_repurpose', family: 'nanite', slot: 'payload', name: 'Repurposing Payload',
    rarity: 'exotic',
    melee: {
      summary: 'Kills convert the corpse into an ally that fights for eight seconds.',
      trigger: 'onKill', damageMul: 1.05,
      statusOnHit: { kind: StatusKind.NANITE_BOUND, duration: 8.0, stacks: 3, chance: 1 },
    },
    proj: {
      summary: 'Kills spawn a replicating swarm that seeks the next target.',
      trigger: 'onKill',
      projectile: { splitOnImpact: 3, homing: 0.7, lifetimeMul: 2.0 },
    },
    risk: {
      kind: 'friendlyFire', magnitude: 0.25,
      warning: 'Repurposed allies decay into hostile swarms when their timer runs out.',
    },
    tags: weaponTags('SUMMONER', 'SELF_RISK'),
    desc: 'The crowd-control answer that eventually becomes the crowd.',
  }));

  out.push(mod({
    id: 'nano_frame_selfrepair', family: 'nanite', slot: 'frame', name: 'Autofab Frame',
    melee: {
      summary: 'The weapon repairs its own degradation and shrugs off corrosion.',
      damageMul: 1.06, weightAdd: -0.06, recoveryMul: 0.92,
    },
    proj: {
      summary: 'Autoloader: faster cadence, and jams repair themselves.',
      cadenceMul: 1.18, damageMul: 0.98,
    },
    desc: 'The safe frame. Nothing dramatic, nothing to manage.',
  }));

  out.push(mod({
    id: 'nano_trigger_assimilate', family: 'nanite', slot: 'trigger', name: 'Assimilation Trigger',
    rarity: 'rare',
    melee: {
      summary: 'On crit: the target is NANITE_BOUND and takes 30% more from every source.',
      trigger: 'onCrit',
      statusOnHit: { kind: StatusKind.NANITE_BOUND, duration: 6.0, stacks: 2, chance: 1 },
    },
    proj: {
      summary: 'On crit: the round infects and homes at every subsequent target.',
      trigger: 'onCrit', projectile: { homing: 0.9, pierceAdd: 2 },
    },
    desc: 'Scales with crit chance, which means it scales with the Kinetic family.',
  }));

  // ----------------------------------------------------------- radiant

  out.push(mod({
    id: 'rad_core_dawn', family: 'radiant', slot: 'core', name: 'Dawn Core',
    rarity: 'rare',
    melee: {
      summary: 'Holy fire against UNDEAD and CURSED; purifies the ground you swing over.',
      damageTypeOverride: D.RADIANT, damageMul: 1.0,
      primes: [P.MARKED],
      terrain: { heat: 120 },
      emit: { mat: MAT.HOLY_FIRE, count: 3, velocity: 30 },
    },
    proj: {
      summary: 'Beams that refract through CRYSTAL and annihilate VOID matter on contact.',
      damageTypeOverride: D.RADIANT, damageMul: 1.05,
      primes: [P.MARKED],
      projectile: { pierceAdd: 2, speedMul: 1.4, ricochet: 1 },
      terrain: { heat: 90 },
    },
    desc: 'Composition-dependent to an extreme degree. Godlike in the crypt, average outside it.',
  }));

  out.push(mod({
    id: 'rad_payload_absolution', family: 'radiant', slot: 'payload', name: 'Absolution Payload',
    rarity: 'exotic',
    melee: {
      summary: 'Heals you while standing on SACRED ground and detonates CURSED for a burst.',
      detonates: [P.CURSED, P.MARKED], detonationMul: 1.6, lifestealFrac: 0.08,
    },
    proj: {
      summary: 'Detonating MARKED calls a vertical column of light down onto the target.',
      detonates: [P.MARKED], detonationMul: 1.7,
      terrain: { heat: 200, destroy: { maxHardness: 3, radius: 6, chance: 0.5 } },
    },
    desc: 'The strongest single detonation in the game, on the narrowest trigger.',
  }));

  out.push(mod({
    id: 'rad_kinetic_ascend', family: 'radiant', slot: 'kinetic', name: 'Ascendant Bearing',
    melee: {
      summary: 'Swings lift you slightly — a rising combo that keeps you off the ground.',
      cadenceMul: 1.06, knockbackMul: 1.15, weightAdd: -0.08,
    },
    proj: {
      summary: 'Weightless rounds: no drop, no drift, and they travel a long way.',
      projectile: { gravityScaleMul: 0, lifetimeMul: 1.6, speedMul: 1.15 },
    },
    tags: weaponTags('MOBILITY'),
    desc: 'Pairs with anything airborne, which is most of this game.',
  }));

  // ---------------------------------------------------------- temporal

  out.push(mod({
    id: 'temp_core_echo', family: 'temporal', slot: 'core', name: 'Echo Core',
    rarity: 'rare',
    melee: {
      summary: 'Every strike repeats 400ms later at 60% damage, wherever the target has moved to.',
      damageTypeOverride: D.TEMPORAL, damageMul: 0.85,
      melee: { echoDelayMs: 400 },
      statusOnHit: { kind: StatusKind.ECHOED, duration: 3.0, stacks: 1, chance: 0.6 },
    },
    proj: {
      summary: 'Rounds land twice: once now, once a beat later at the same spot.',
      damageTypeOverride: D.TEMPORAL, damageMul: 0.8,
      projectile: { countAdd: 1, lifetimeMul: 1.1 },
      statusOnHit: { kind: StatusKind.ECHOED, duration: 2.5, stacks: 1, chance: 0.6 },
    },
    desc: 'Lower per-hit numbers that come out ahead against anything that stands still.',
  }));

  out.push(mod({
    id: 'temp_payload_stasis', family: 'temporal', slot: 'payload', name: 'Stasis Payload',
    rarity: 'exotic',
    melee: {
      summary: 'The finisher drops a slow field. Everything inside it moves at 40% speed.',
      statusOnHit: { kind: StatusKind.SLOWED, duration: 3.0, stacks: 2, chance: 1 },
      trigger: 'onFinisher', detonates: [P.PHASED], detonationMul: 1.35,
    },
    proj: {
      summary: 'Delayed-impact rounds hang in the air, then all detonate at once.',
      statusOnHit: { kind: StatusKind.SLOWED, duration: 2.0, stacks: 1, chance: 0.8 },
      projectile: { speedMul: 0.5, lifetimeMul: 2.5, splitOnImpact: 2 },
    },
    risk: {
      kind: 'selfStatus', magnitude: 1, status: StatusKind.SLOWED,
      warning: 'The slow field does not distinguish between bodies. Including yours.',
    },
    tags: weaponTags('SELF_RISK'),
    desc: 'Enormous control that costs you the movement pillar while it is active.',
  }));

  out.push(mod({
    id: 'temp_frame_rewound', family: 'temporal', slot: 'frame', name: 'Rewound Frame',
    rarity: 'rare',
    melee: {
      summary: 'Whiffed swings cost almost nothing: recovery is cut by 35% on a miss.',
      recoveryMul: 0.65, windupMul: 0.9,
    },
    proj: {
      summary: 'Missed shots partially refund; effective cadence rises the worse you aim.',
      cadenceMul: 1.25, damageMul: 0.9,
    },
    desc: 'A forgiveness frame. Makes committal weapons playable for people who are still learning them.',
  }));

  out.push(mod({
    id: 'temp_trigger_reprise', family: 'temporal', slot: 'trigger', name: 'Reprise Trigger',
    melee: {
      summary: 'On hit-confirm: the combo timer resets, so the finisher can repeat immediately.',
      trigger: 'onHit', recoveryMul: 0.8,
    },
    proj: {
      summary: 'On hit-confirm: the next shot within 300ms is free.',
      trigger: 'onHit', cadenceMul: 1.3,
    },
    desc: 'Rewards accuracy directly, which is rarer in this catalogue than it should be.',
  }));

  // ----------------------------------------------------------- kinetic

  out.push(mod({
    id: 'kin_core_impact', family: 'kinetic', slot: 'core', name: 'Impact Core',
    melee: {
      summary: 'Pure force: no element, huge poise break, and the terrain gives way.',
      damageTypeOverride: D.PHYSICAL, damageMul: 1.15, poiseMul: 1.6, knockbackMul: 1.5,
      primes: [P.STAGGERED],
      terrain: { destroy: { maxHardness: 5, radius: 8, chance: 0.8 }, displaces: true },
    },
    proj: {
      summary: 'Solid slugs: they pierce, and they punch through walls rather than stopping at them.',
      damageTypeOverride: D.PHYSICAL, damageMul: 1.2,
      primes: [P.STAGGERED],
      projectile: { pierceAdd: 2, speedMul: 1.2, recoilMul: 1.3 },
      terrain: { destroy: { maxHardness: 4, radius: 5, chance: 0.9 } },
    },
    tags: weaponTags('DIGGER'),
    desc: 'The core you take when the enemy composition is unknown. Nothing resists physical.',
  }));

  out.push(mod({
    id: 'kin_kinetic_recoil', family: 'kinetic', slot: 'kinetic', name: 'Recoil Harness',
    rarity: 'uncommon',
    melee: {
      summary: 'The swing throws you with it — every attack is a small dash.',
      knockbackMul: 1.4, cadenceMul: 1.05, weightAdd: 0.05,
    },
    proj: {
      summary: 'Triples recoil and converts it into clean directional movement.',
      projectile: { recoilMul: 3.0, spreadMul: 1.15 },
    },
    tags: weaponTags('MOBILITY'),
    desc: 'Recoil-as-mobility, stated plainly. A scattergun with this is a traversal tool.',
  }));

  out.push(mod({
    id: 'kin_frame_counterweight', family: 'kinetic', slot: 'frame', name: 'Counterweight Frame',
    melee: {
      summary: 'Adds mass. More damage and knockback, slower everything.',
      weightAdd: 0.2, damageMul: 1.3, cadenceMul: 0.78, recoveryMul: 1.2, knockbackMul: 1.4,
    },
    proj: {
      summary: 'Braced frame: kills recoil entirely at the cost of rate of fire.',
      weightAdd: 0.15, damageMul: 1.22, cadenceMul: 0.82,
      projectile: { recoilMul: 0.25, spreadMul: 0.7 },
    },
    desc: 'The clearest trade in the catalogue, which is why it is the tutorial frame.',
  }));

  out.push(mod({
    id: 'kin_frame_featherweight', family: 'kinetic', slot: 'frame', name: 'Featherweight Frame',
    melee: {
      summary: 'Strips mass. Much faster, much lighter hits, and one extra swing before the chain loops.',
      weightAdd: -0.22, damageMul: 0.78, cadenceMul: 1.35, recoveryMul: 0.78,
      // `extraChainHits` had no author anywhere in the catalogue and no reader
      // in the runtime, so it was dead at both ends (issue #15). It lands here
      // because this frame's whole identity is more, lighter hits, and because
      // the −22% damage it already charges is the trade §9 asks for: the extra
      // swing is longer commitment on a chain that hits softer.
      melee: { extraChainHits: 1 },
    },
    proj: {
      summary: 'Stripped frame: high cadence, low damage, negligible recoil.',
      weightAdd: -0.18, damageMul: 0.8, cadenceMul: 1.4,
      projectile: { recoilMul: 0.4 },
    },
    desc: 'The exact inverse of Counterweight. Neither is better; that is the point.',
  }));

  out.push(mod({
    id: 'kin_trigger_slam', family: 'kinetic', slot: 'trigger', name: 'Slam Trigger',
    rarity: 'uncommon',
    melee: {
      summary: 'On a down-air pogo: a shockwave breaks the floor and staggers everything on it.',
      trigger: 'onFinisher', poiseMul: 2.0,
      terrain: { destroy: { maxHardness: 4, radius: 16, chance: 0.7 }, displaces: true },
      statusOnHit: { kind: StatusKind.STAGGERED, duration: 1.4, stacks: 1, chance: 0.9 },
    },
    proj: {
      summary: 'On a downward shot: the recoil launches you and the impact cracks the ground.',
      trigger: 'onFinisher',
      projectile: { recoilMul: 2.2 },
      terrain: { destroy: { maxHardness: 3, radius: 12, chance: 0.6 }, displaces: true },
    },
    tags: weaponTags('DIGGER', 'MOBILITY'),
    desc: 'Turns the universal pogo into a terrain tool.',
  }));

  // ---------------------------------------------------------- resonant

  out.push(mod({
    id: 'res_core_harmonic', family: 'resonant', slot: 'core', name: 'Harmonic Core',
    rarity: 'rare',
    melee: {
      summary: 'Damage scales on resonance instead of might, and wards open for you.',
      damageMul: 1.0, critChanceAdd: 0.04,
      statusOnHit: { kind: StatusKind.RESONATING, duration: 4.0, stacks: 1, chance: 0.5 },
    },
    proj: {
      summary: 'Focused beam that amplifies each time it passes through CRYSTAL.',
      damageMul: 0.95,
      projectile: { pierceAdd: 3, ricochet: 3, speedMul: 1.2 },
      statusOnHit: { kind: StatusKind.RESONATING, duration: 3.0, stacks: 1, chance: 0.6 },
    },
    tags: weaponTags('RESONANT'),
    desc: 'Only worth running on a relic weapon or in a crystal biome. Then it is the best core here.',
  }));

  out.push(mod({
    id: 'res_payload_chorus', family: 'resonant', slot: 'payload', name: 'Chorus Payload',
    rarity: 'exotic',
    melee: {
      summary: 'Each RESONATING target amplifies every other one within earshot.',
      detonates: [P.MARKED], detonationMul: 1.5,
      statusOnHit: { kind: StatusKind.RESONATING, duration: 5.0, stacks: 2, chance: 0.8 },
    },
    proj: {
      summary: 'Harmonic chain: shots link resonating targets and split the damage between them.',
      detonates: [P.MARKED], detonationMul: 1.35,
      projectile: { ricochet: 4, homing: 0.5 },
      statusOnHit: { kind: StatusKind.RESONATING, duration: 4.0, stacks: 1, chance: 0.9 },
    },
    desc: 'Scales quadratically with crowd size, which is why its base numbers look weak.',
  }));

  out.push(mod({
    id: 'res_kinetic_tuned', family: 'resonant', slot: 'kinetic', name: 'Tuned Bearing',
    melee: {
      summary: 'Perfectly balanced: every timing tightens a little, nothing gets worse.',
      cadenceMul: 1.08, windupMul: 0.92, recoveryMul: 0.94, damageMul: 0.97,
    },
    proj: {
      summary: 'Tuned barrel: tighter spread, marginally slower rounds.',
      projectile: { spreadMul: 0.6, speedMul: 0.95 },
      cadenceMul: 1.05,
    },
    desc: 'The boring pick, and correct more often than it should be.',
  }));

  out.push(mod({
    id: 'res_trigger_ward', family: 'resonant', slot: 'trigger', name: 'Warding Trigger',
    rarity: 'rare',
    melee: {
      summary: 'On parry: a resonant ward absorbs the next hit against you entirely.',
      trigger: 'onParry', melee: { parryWindowAdd: 60 },
    },
    proj: {
      summary: 'On swap: the incoming weapon fires its first shot at triple power.',
      trigger: 'onSwap', damageMul: 3.0,
    },
    tags: weaponTags('PARRY'),
    desc: 'The projectile half rewards dual-wield swapping, which is the point of the whole system.',
  }));

  // ------------------------------------------- cross-family self-risk exotics

  out.push(mod({
    id: 'exotic_payload_martyr', family: 'blood', slot: 'payload', name: 'Martyr\'s Charge',
    rarity: 'relic',
    melee: {
      summary: 'Your finisher deals damage equal to 25% of the health you are missing.',
      damageMul: 1.0, lifestealFrac: 0.0, trigger: 'onFinisher',
      detonates: [P.BLEEDING, P.CURSED], detonationMul: 2.0,
    },
    proj: {
      summary: 'Each shot costs 6% max health and deals triple damage.',
      damageMul: 3.0, cadenceMul: 0.7, trigger: 'onHit',
    },
    risk: {
      kind: 'healthCost', magnitude: 0.06,
      warning: 'Six percent of your maximum health per shot, with no lifesteal to pay it back.',
    },
    tags: weaponTags('SELF_RISK'),
    desc: 'The most dangerous module in the game and, in the right hands, the strongest.',
  }));

  out.push(mod({
    id: 'exotic_core_unstable', family: 'void', slot: 'core', name: 'Unstable Singularity',
    rarity: 'relic',
    melee: {
      summary: 'Every third swing collapses a well that damages everything, including you.',
      damageTypeOverride: D.VOID, damageMul: 1.5, knockbackMul: -1.8,
      terrain: { displaces: true, destroy: { maxHardness: 6, radius: 18, chance: 0.8 } },
    },
    proj: {
      summary: 'Rounds collapse into singularities on impact. The blast radius exceeds your reach.',
      damageTypeOverride: D.VOID, damageMul: 1.6,
      projectile: { speedMul: 0.75, splitOnImpact: 1 },
      terrain: { displaces: true, destroy: { maxHardness: 6, radius: 22, chance: 0.7 } },
    },
    risk: {
      kind: 'friendlyFire', magnitude: 0.7,
      warning: 'The collapse radius is larger than your weapon reach. You will be inside it.',
    },
    tags: weaponTags('SELF_RISK', 'DIGGER', 'AOE'),
    desc: 'Best digging tool in the game. Also the leading cause of death among people who run it.',
  }));

  out.push(mod({
    id: 'exotic_frame_conduit', family: 'galvanic', slot: 'frame', name: 'Open Conduit Frame',
    rarity: 'relic',
    melee: {
      summary: 'Damage scales with ambient charge. Standing in a storm doubles it.',
      damageMul: 1.35, cadenceMul: 1.1,
      terrain: { charge: 1.0 },
    },
    proj: {
      summary: 'Fires without ammunition, drawing from ambient electricity instead.',
      damageMul: 1.3, cadenceMul: 1.2,
      terrain: { charge: 1.0 },
    },
    risk: {
      kind: 'selfStatus', magnitude: 1, status: StatusKind.SHOCKED,
      warning: 'You conduct. Standing in water while it is charged shocks you every tick.',
    },
    tags: weaponTags('SELF_RISK'),
    desc: 'Free ammunition, at the price of never being able to stand in a puddle again.',
  }));

  out.push(mod({
    id: 'exotic_trigger_pyreburst', family: 'pyre', slot: 'trigger', name: 'Immolation Trigger',
    rarity: 'exotic',
    melee: {
      summary: 'On kill: you catch fire, and while burning your damage is up 45%.',
      trigger: 'onKill', damageMul: 1.45,
      emit: { mat: MAT.FIRE, count: 16, velocity: 70 },
    },
    proj: {
      summary: 'On kill: a ring of fire erupts from the corpse and spreads to accelerants.',
      trigger: 'onKill',
      emit: { mat: MAT.FIRE, count: 26, velocity: 90 },
      terrain: { heat: 400 },
    },
    risk: {
      kind: 'selfStatus', magnitude: 1, status: StatusKind.BURNING,
      warning: 'You are set on fire by your own kills. Water cancels the bonus.',
    },
    tags: weaponTags('SELF_RISK'),
    desc: 'A snowball module. It is only a problem when you stop killing things.',
  }));

  return out;
}

/** Index by id, with a duplicate guard. */
export function indexModules(defs: readonly ModuleDef[]): Map<string, ModuleDef> {
  const m = new Map<string, ModuleDef>();
  for (const d of defs) {
    if (m.has(d.id)) throw new Error(`duplicate module id: ${d.id}`);
    m.set(d.id, d);
  }
  return m;
}
